/**
 * main.js — ponto de entrada do POC "WAB Smart Town".
 *
 * Fluxo:
 *   1. monta a cena (cidade no estilo da referência)
 *   2. conecta ao "backend" (PlayerService, hoje simulado via JSON)
 *   3. cada snapshot de coordenadas move carros/pessoas dos jogadores
 */
import { SceneManager } from './scene/SceneManager.js';
import { createBuildings } from './world/Buildings.js';
import { createTrees } from './world/Trees.js';
import { createLightPosts } from './world/LightPosts.js';
import { createClouds, updateClouds } from './world/Clouds.js';
import { createDecor, updateLedMarquees } from './world/Decor.js';
import { createBenches } from './world/Benches.js';
import { batchStatic, MAT_MAPS } from './world/staticBatch.js';
import { EntityManager } from './entities/EntityManager.js';
import { PlayerService } from './net/PlayerService.js';
import { Hud } from './ui/Hud.js';
import { Settings } from './ui/Settings.js';
import { Achievements } from './ui/Achievements.js';
import { randomAppearance } from './entities/personModel.js';
import { randomCarAppearance } from './entities/carModel.js';
import { renderAvatarPortrait } from './ui/AvatarPortrait.js';
import { BuildingDialog } from './ui/BuildingDialog.js';
import { applyI18n, t } from './i18n.js';
import { probes, P } from './perfProbes.js';
import { IS_ULTRA } from './scene/qualityLevel.js';
import { CITY_NAME } from './world/cityLayout.js';
import * as cityLayout from './world/cityLayout.js';

/**
 * ANISOTROPIA MÁXIMA em todas as texturas do cenário — só no nível Ultra.
 *
 * A câmera é isométrica a ~50° de elevação: chão, calçada, asfalto e TELHADO (a
 * maior superfície visível de um prédio nesta vista) estão rasantes o quadro
 * inteiro, que é exatamente o caso em que a filtragem trilinear sozinha borra a
 * textura em faixas. Só 4 texturas do projeto tinham anisotropia (fixa em 4/8) e
 * `getMaxAnisotropy()` nunca era chamado; aqui todas as que têm mipmap sobem para
 * o máximo do driver (tipicamente 16). Custo: ~10-20% do tempo de fragmento
 * dessas superfícies, ZERO de VRAM.
 *
 * A ORDEM importa duas vezes. Depois do batchStatic, porque ele deduplica
 * materiais por assinatura (que inclui os maps) e marcar needsUpdate antes
 * reenviaria texturas que seriam descartadas em seguida. E depois do
 * EntityManager, porque a mancha de sombra dos avatares nasce ali — rodando
 * antes, ela ficava de fora (era 88 de 91 texturas da cena).
 *
 * `needsUpdate` é obrigatório: a anisotropia entra na chave de cache da textura
 * no three, então sem ele a mudança é um no-op silencioso.
 *
 * Os rótulos dos jogadores NÃO entram (e nem deveriam): eles desligam mipmap de
 * propósito — ver makeLabel.js — e anisotropia sem mipmap não faz nada.
 */
function aplicarAnisotropia(stage) {
  const aniso = stage.maxAniso || 1;
  if (!IS_ULTRA || aniso <= 1) return;
  const vistas = new Set();
  stage.scene.traverse((o) => {
    const mats = Array.isArray(o.material) ? o.material : o.material ? [o.material] : [];
    for (const m of mats) {
      for (const slot of MAT_MAPS) {
        const t = m[slot];
        // sem mipmap a anisotropia não faz nada (e reenviar a textura custaria à toa)
        if (!t || t.generateMipmaps === false || vistas.has(t)) continue;
        vistas.add(t);
        if (t.anisotropy === aniso) continue;
        t.anisotropy = aniso;
        t.needsUpdate = true;
      }
    }
  });
  console.info(`[ultra] anisotropia ${aniso}x em ${vistas.size} texturas`);
}

async function boot() {
  const hud = new Hud();
  hud.setLoaderProgress(0.1);

  applyI18n(); // textos estáticos do HUD no idioma da cidade (ver i18n.js)

  // Título da aba com a cidade ativa (1 das 4 — ver cities.js). O nome não é mais
  // exibido no HUD (city-tag removido); segue no seletor de Cidade das Configurações.
  document.title = `${CITY_NAME} — WAB Smart Town`;

  // 1) Cena + cidade
  const stage = new SceneManager(document.getElementById('app'));
  // TETO DE FPS via URL (?fps=30) — p/ telão/kiosk de 8h: metade do trabalho
  // térmico, bloom mantido. Sem o parâmetro → sem teto (fps do monitor).
  const fpsCap = parseInt(new URLSearchParams(location.search).get('fps'), 10);
  if (Number.isFinite(fpsCap) && fpsCap > 0) stage.fpsCap = fpsCap;
  hud.setLoaderProgress(0.4);

  // batchStatic: funde o cenário ESTÁTICO em ~1 mesh por material (draw calls
  // ~3.4k → ~centenas; visual idêntico). Só nos grupos que nunca mudam — árvores
  // (balançam ao esbarrão) e nuvens (deriva) ficam de fora.
  stage.add(batchStatic(createBuildings()));
  hud.setLoaderProgress(0.6);
  const trees = createTrees(); // arbustos reagem ao serem esbarrados pelas pessoas
  stage.add(trees.group);
  stage.add(batchStatic(createLightPosts()));
  const clouds = createClouds();
  stage.add(clouds);
  stage.add(batchStatic(createDecor()));
  stage.add(batchStatic(createBenches()));
  hud.setLoaderProgress(0.8);

  // 2) "Backend" (simulado) — dinâmica dos jogadores (entrar/vagar/estacionar/sair)
  const service = new PlayerService();
  await service.connect().catch((err) => console.error('[PlayerService]', err));

  // o renderer habilita a atualizacao PARCIAL da textura do atlas de nomes
  // (texSubImage2D da celula, em vez de reenviar a pagina de 2048x2048)
  const entities = new EntityManager(stage.scene, service.tickMs, stage.renderer, stage.overlay);
  aplicarAnisotropia(stage); // depois do EntityManager: pega a mancha de sombra dos avatares
  const achievements = new Achievements(); // banner de conquista (topo-central)
  const announcements = new BuildingDialog(); // balão de fala do edifício (recepcionista)
  const playersById = new Map();           // id → jogador (do snapshot), p/ resolver aparências

  // CONFETE DE CONQUISTA COM ESPERA. O RealBackend aplica o inbox no INÍCIO do tick e o
  // EntityManager só conhece o jogador no snapshot emitido no FIM dele — então um
  // SPAWN+ACHIEVEMENT no MESMO lote (o que o bridge emite quando o parceiro manda
  // `achievement` sem `join` antes) disparava o burst para um id que ainda não existe em
  // cena: no-op silencioso, conquista sem confete. Guardamos o pedido e tentamos a cada
  // quadro até o avatar aparecer — inclusive quando ele ficou na FILA de entrada (boca da
  // via lotada), que na rajada de 200 entradas segurou 189 delas no 1º tick.
  const confetePendente = new Map(); // id → prazo (ms) p/ desistir
  const CONFETE_ESPERA = 8000;       // teto: quem não entrou até aqui não entra mais (leave/lotação)
  const pedirConfete = (id) => {
    if (entities.getFollowTarget(id)) { entities.burstConfetti(id); return; } // já em cena → agora
    confetePendente.set(id, performance.now() + CONFETE_ESPERA);
  };
  const drenarConfete = () => {
    if (!confetePendente.size) return; // caso comum: nada a fazer, sem custo por quadro
    const agora = performance.now();
    for (const [id, prazo] of confetePendente) {
      if (entities.getFollowTarget(id)) { confetePendente.delete(id); entities.burstConfetti(id); }
      else if (agora > prazo) confetePendente.delete(id);
    }
  };

  // liga as reações do backend ao renderer/HUD (no-op no modo simulado)
  service.setEffects({
    onEmote: (id, emoji, dur) => entities.emote(id, emoji, dur),
    onSay: (id, text, dur, tone) => entities.say(id, text, dur, tone),
    // troca de visual ao vivo (backend manda quando o vendor atualiza o appearance)
    onRestyle: (id, app) => entities.restyle(id, app),
    // missões: o backend manda a somatória acumulada do grupo de placar a que esta
    // cidade pertence — as três do funil dividem o mesmo número (score não é exibido)
    onScore: (sc, missions) => { hud.setMissions(missions); },
    // conquista de um jogador → banner no topo. O backend manda { id, message, points,
    // name, appearance } e o frame VENCE: `playersById` é preenchido no FIM do tick do
    // PlayerService, enquanto o inbox do backend é aplicado no INÍCIO do MESMO tick — num
    // SPAWN+ACHIEVEMENT do mesmo lote (rajada do evento) o id ainda não está no índice e o
    // banner saía com "Jogador" + avatar genérico. A resolução por id fica só como
    // fallback p/ servidor ANTIGO, que não manda nome/aparência no frame.
    onAchievement: (a) => {
      const p = a && a.id ? playersById.get(a.id) : null;
      const name = a.name || p?.name || t('Jogador');
      const carApp = a.carAppearance || p?.carAppearance;
      const personApp = a.personAppearance || p?.personAppearance;
      // cor do chip: sem o jogador no índice, a pintura do carro (ou a camisa) é a mesma
      // cor que o servidor usou p/ montar a aparência — o dot de fallback não fica cinza
      const color = p?.color || carApp?.colors?.paint || personApp?.colors?.top;
      achievements.show({
        name,
        message: a.message,
        points: a.points,
        carAppearance: carApp,
        personAppearance: personApp
      });
      if (a && a.id) pedirConfete(a.id); // confete ao redor do avatar (espera ele entrar em cena)
      // faixa "missões concluídas" no rodapé: o chip entra COMEMORANDO (retrato vivo,
      // 3 pulos + confete — as mesmas animações do avatar na cidade) e depois assenta.
      //
      // Passamos as APARÊNCIAS, não um PNG pronto: rasterizar o retrato estático aqui
      // custava um render WebGL extra mais a leitura do framebuffer e a codificação do
      // PNG, ambas síncronas na thread principal, no mesmo quadro do banner — e a
      // imagem só seria usada 3 s depois. O último quadro do chip vivo já é ela.
      hud.addAchiever({
        id: a.id, name, color, points: a.points,
        carAppearance: carApp, personAppearance: personApp
      });
    },
    // fala do EDIFÍCIO: o backend manda { building, text, title?, tone?, durationMs? }
    // e o balão do "recepcionista" sai do prédio para todos os viewers da cidade.
    onAnnounce: (a) => announcements.show(a)
  });
  // PERFIL FRACO p/ o Hud: com a resolução em degrau baixo (manual "desempenho" ou
  // escada automática no fundo), monta no máximo 1 celebração de chip por quadro.
  hud.perfilFraco = () => stage.maxPixelRatio <= 0.85;
  // popup de configurações (sol, nuvens, câmera, etiquetas, população simulada, cidade)
  new Settings(stage, entities, service, hud, clouds);
  service.onSnapshot((snap) => {
    probes.begin(P.snapshot);
    entities.onSnapshot(snap);
    // mantém a contagem "online" em sincronia com a população
    hud.setOnline(snap.players.length);
    // índice de jogadores → resolve nome/cor/aparências da conquista (onAchievement) pelo id
    playersById.clear();
    for (const p of snap.players) playersById.set(p.id, p);
    probes.end(P.snapshot);
  });

  hud.setOnline(service.population);
  if (service.live) {
    hud.setMissions(0); // ao vivo: o backend (placar no HELLO) define em seguida
  } else {
    hud.setMissions(8); // demo
  }
  hud.setLoaderProgress(1);

  // 3) Loop
  const _people = []; // buffer reaproveitado [x,z,x,z,...] p/ a interação dos arbustos
  stage.addUpdater((dt, t) => {
    // simulação + snapshot DENTRO do quadro (ver PlayerService.pump): a tarefa de
    // ~2,5 ms deixou de cair entre dois quadros e estourar o slot do vsync
    service.pump();
    probes.begin(P.entidades);
    entities.update(dt, stage.camera, stage.shadowGroundDir());
    probes.end(P.entidades);
    probes.begin(P.controles); // câmera dinâmica soma com o OrbitControls do tick
    if (stage.dynamicCam) stage.updateDynamicCamera(dt, entities.cameraFocus());
    probes.end(P.controles);
    probes.begin(P.arvores);
    trees.update(dt, entities.collectPeople(_people));
    probes.end(P.arvores);
    probes.begin(P.nuvens);
    if (clouds.visible) updateClouds(clouds, dt); // desligadas nas Configurações → nem atualiza
    probes.end(P.nuvens);
    updateLedMarquees(dt); // rola o texto dos painéis de LED das praças
    probes.begin(P.hud);   // hud + banner de conquista (inclui os renders auxGL)
    drenarConfete();       // conquista que chegou antes do avatar entrar em cena
    // DEFASAGEM banner × chips: se os chips renderizaram neste quadro, o retrato
    // do banner espera o próximo — nunca 2+ renders auxiliares no mesmo quadro
    const chipsDesenharam = hud.update(dt);
    achievements.update(chipsDesenharam);
    probes.end(P.hud);
    probes.begin(P.dialogos);
    announcements.update(stage.camera); // reposiciona/expira os balões dos edifícios
    probes.end(P.dialogos);
  });
  stage.start();

  hud.hideLoader();

  // PRÉ-AQUECE os mini-renderers de conquista na OCIOSIDADE. A primeira conquista da
  // sessão criava os contextos WebGL do banner e dos chips e compilava os shaders das
  // peças e do confete — tudo no mesmo quadro em que o banner entra, o maior pico do
  // caminho. Feito aqui, o custo cai num momento em que ninguém está olhando e toda
  // conquista (inclusive a primeira) segue o mesmo caminho barato.
  // Camisa/calça, vestido e cabelo cobrem os shaders de braço, perna, tecido e acabamento;
  // sorteio irrestrito poderia deixar a primeira pose sentada compilar no quadro.
  const aquecer = () => {
    const pares = [0, 1].map(i => [randomCarAppearance(), {
      ...randomAppearance(),hair:i===0?'short':'long',top:i===0?'shirt':'dress',bottom:i===0?'pants':'none',feet:i===0?'shoes':'sandals'
    }]);
    try {
      achievements.prewarm(pares);
      hud.prewarmAchievers(pares);
      // o retrato ESTÁTICO (fallback dos chips) também: sem isto, a 1ª conquista em
      // máquina sem celebração viva criava o 3º contexto WebGL no próprio quadro
      renderAvatarPortrait(pares[0][0], pares[0][1]);
    } catch (e) {
      console.warn('pré-aquecimento das conquistas falhou (segue lazy)', e);
    }
  };
  if (window.requestIdleCallback) requestIdleCallback(aquecer, { timeout: 4000 });
  else setTimeout(aquecer, 1200);

  // expõe para depuração no console
  window.__wabs = { stage, service, entities, hud, trees, achievements, announcements, city: cityLayout };
}

/**
 * O TELÃO PRECISA SE LEVANTAR SOZINHO. Quando a GPU perde o contexto (reset de driver,
 * atualização, volta de suspensão), o SceneManager agenda um `location.reload()` — e é
 * justamente aí que a GPU tem mais chance de ainda estar indisponível: o `new
 * WebGLRenderer` do boot seguinte lança, `boot()` é async e a exceção virava uma rejeição
 * não tratada. O loader ficava em 40% e a recuperação nem chegava a ser instalada, então
 * NÃO havia segunda tentativa: o telão passava o resto da madrugada parado.
 *
 * Aqui a falha vira uma nova tentativa com espera crescente, contada em sessionStorage
 * para não virar um laço apertado de reloads.
 */
const CHAVE_TENTATIVA = 'wabs.bootRetry';
boot().catch((err) => {
  console.error('[boot] falhou — vou tentar de novo', err);
  let n = 0;
  try { n = Number(sessionStorage.getItem(CHAVE_TENTATIVA)) || 0; } catch { /* sem storage */ }
  const espera = Math.min(5000 * 2 ** n, 60000); // 5s, 10s, 20s, 40s, teto 60s
  try { sessionStorage.setItem(CHAVE_TENTATIVA, String(n + 1)); } catch { /* sem storage */ }
  const el = document.getElementById('loader');
  if (el) {
    const aviso = document.createElement('div');
    aviso.className = 'loader-erro';
    aviso.textContent = `${t('Reiniciando a cidade…')} (${n + 1})`;
    el.appendChild(aviso);
  }
  setTimeout(() => location.reload(), espera);
});
// boot completo → a contagem de tentativas volta a zero
window.addEventListener('load', () => {
  setTimeout(() => { try { sessionStorage.removeItem(CHAVE_TENTATIVA); } catch { /* sem storage */ } }, 30000);
});
